import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { Bot, Loader2, Minus, Send, X } from "lucide-react";
import AiCards from "./AiCards";
import { API_BASE } from "../../lib/api";

const HIDDEN = ["/admin", "/ai-admin", "/crm", "/pickup", "/ccare"];
const LANGS = [{ c: "en", l: "English" }, { c: "te", l: "తెలుగు" }, { c: "hi", l: "हिन्दी" }];

function sessionId() {
  let s = localStorage.getItem("if_ai_sid");
  if (!s) {
    s = window.crypto?.randomUUID ? crypto.randomUUID() : `s-${Date.now()}-${Math.random()}`;
    localStorage.setItem("if_ai_sid", s);
  }
  return s;
}

export default function AiAssistant() {
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const [minimized, setMinimized] = useState(false);
  const [msgs, setMsgs] = useState([]);
  const [qrs, setQrs] = useState([]);
  const [input, setInput] = useState("");
  const [typing, setTyping] = useState(false);
  const [lang, setLang] = useState("en");
  const greeted = useRef(false);
  const boxRef = useRef(null);
  const busy = useRef(false);

  const hidden = HIDDEN.some((p) => pathname.startsWith(p));
  const scroll = () => setTimeout(() => boxRef.current?.scrollTo({ top: 9999999, behavior: "smooth" }), 40);

  async function send(text, action) {
    if ((!text && !action) || busy.current) return;
    busy.current = true;
    if (text) setMsgs((m) => [...m, { role: "user", text, cards: [] }]);
    setInput("");
    setTyping(true);
    setQrs([]);
    setMsgs((m) => [...m, { role: "assistant", text: "", cards: [], streaming: true }]);
    scroll();
    try {
      const res = await fetch(`${API_BASE}/api/ai/chat`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId(), message: text || "", action: action || null }),
      });
      const reader = res.body.getReader();
      const dec = new TextDecoder();
      let buf = "";
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        let i;
        while ((i = buf.indexOf("\n\n")) >= 0) {
          const raw = buf.slice(0, i).trim();
          buf = buf.slice(i + 2);
          if (!raw.startsWith("data:")) continue;
          let ev;
          try { ev = JSON.parse(raw.slice(5).trim()); } catch { continue; }
          if (ev.type === "delta") {
            setMsgs((m) => { const c = [...m]; c[c.length - 1] = { ...c[c.length - 1], text: c[c.length - 1].text + ev.text }; return c; });
            scroll();
          } else if (ev.type === "done") {
            setMsgs((m) => { const c = [...m]; c[c.length - 1] = { role: "assistant", text: c[c.length - 1].text, cards: ev.cards || [] }; return c; });
            setQrs(ev.quick_replies || []);
            const base = { "te-latn": "te", "hi-latn": "hi" }[ev.language] || ev.language;
            if (["en", "te", "hi"].includes(base)) setLang(base);
            scroll();
          }
        }
      }
    } catch {
      setMsgs((m) => { const c = [...m]; c[c.length - 1] = { role: "assistant", text: "Sorry, I'm unable to retrieve that information right now. I can create a request for our IndiaFast customer-care team — type Human anytime.", cards: [] }; return c; });
    } finally {
      busy.current = false;
      setTyping(false);
      scroll();
    }
  }

  const openPanel = useCallback(async (action) => {
    setOpen(true);
    setMinimized(false);
    if (!greeted.current) {
      greeted.current = true;
      try {
        const c = await fetch(`${API_BASE}/api/ai/config`).then((r) => r.json());
        setMsgs([{ role: "assistant", text: c.greeting, cards: [] }]);
        setQrs(c.quick_replies || []);
      } catch {
        setMsgs([{ role: "assistant", text: "Hi! Welcome to IndiaFast Global Couriers. How can I help you today?", cards: [] }]);
      }
      fetch(`${API_BASE}/api/ai/event`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId(), type: "chat_open" }),
      }).catch(() => {});
      scroll();
    }
    if (action) send(null, action);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const h = (e) => openPanel(e.detail?.action);
    window.addEventListener("if-ai:open", h);
    return () => window.removeEventListener("if-ai:open", h);
  }, [openPanel]);

  if (hidden) return null;

  return (
    <>
      {!open && (
        <button onClick={() => openPanel()} data-testid="ai-assistant-fab" aria-label="Need help? Ask IndiaFast Assistant"
                className="group fixed bottom-20 right-4 z-[95] sm:bottom-6 sm:right-6">
          <span className="absolute inset-0 animate-ping rounded-full bg-saffron/30" />
          <span className="relative grid h-14 w-14 place-items-center rounded-full bg-saffron text-white shadow-xl shadow-saffron/40 transition-transform group-hover:scale-105">
            <Bot className="h-7 w-7" />
          </span>
          <span className="pointer-events-none absolute right-16 top-1/2 hidden -translate-y-1/2 whitespace-nowrap rounded-lg bg-navy px-3 py-1.5 text-xs font-semibold text-white opacity-0 shadow-lg transition-opacity group-hover:opacity-100 sm:block">
            Need help? Ask IndiaFast Assistant
          </span>
        </button>
      )}

      {open && (
        <div data-testid="ai-chat-panel"
             className="fixed inset-0 z-[96] flex flex-col bg-slate-50 shadow-2xl sm:inset-auto sm:bottom-24 sm:right-6 sm:h-[min(650px,82vh)] sm:w-[410px] sm:rounded-2xl sm:border sm:border-slate-200">
          <div className="flex items-center gap-3 bg-navy px-4 py-3 sm:rounded-t-2xl" data-testid="ai-chat-header">
            <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-saffron text-white">
              <Bot className="h-5 w-5" />
            </span>
            <div className="min-w-0 flex-1">
              <p className="truncate font-heading text-sm font-bold text-white">IndiaFast Assistant</p>
              <p className="flex items-center gap-1.5 text-[11px] text-emerald-400">
                <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" /> Online · 24×7 Support
              </p>
            </div>
            <div className="flex items-center gap-1">
              {LANGS.map((l) => (
                <button key={l.c} onClick={() => { setLang(l.c); send(null, `lang:${l.c}`); }}
                        data-testid={`ai-lang-${l.c}`}
                        className={`rounded-md px-1.5 py-1 text-[10px] font-bold transition-colors ${lang === l.c ? "bg-saffron text-white" : "text-slate-300 hover:text-white"}`}>
                  {l.l}
                </button>
              ))}
              <button onClick={() => setMinimized(!minimized)} data-testid="ai-minimize-btn" aria-label="Minimize"
                      className="ml-1 rounded-md p-1.5 text-slate-300 transition-colors hover:bg-white/10 hover:text-white max-sm:hidden">
                <Minus className="h-4 w-4" />
              </button>
              <button onClick={() => setOpen(false)} data-testid="ai-close-btn" aria-label="Close"
                      className="rounded-md p-1.5 text-slate-300 transition-colors hover:bg-white/10 hover:text-white">
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>

          {!minimized && (
            <>
              <div ref={boxRef} className="flex-1 space-y-3 overflow-y-auto px-3 py-4" data-testid="ai-messages">
                {msgs.map((m, i) => (
                  <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
                       data-testid={`ai-msg-${m.role}-${i}`}>
                    <div className={`max-w-[85%] ${m.role === "user" ? "rounded-2xl rounded-br-sm bg-navy px-3.5 py-2.5 text-white" : "rounded-2xl rounded-bl-sm bg-white px-3.5 py-2.5 text-navy shadow-sm border border-slate-100"}`}>
                      {m.streaming && !m.text
                        ? <p className="flex items-center gap-2 text-xs text-slate-400"><Loader2 className="h-3.5 w-3.5 animate-spin" /> IndiaFast Assistant is typing…</p>
                        : <p className="whitespace-pre-line text-sm leading-relaxed">{m.text}</p>}
                      <AiCards cards={m.cards} />
                    </div>
                  </div>
                ))}
              </div>

              {qrs.length > 0 && !typing && (
                <div className="flex flex-wrap gap-1.5 px-3 pb-2" data-testid="ai-quick-replies">
                  {qrs.map((q) => (
                    <button key={q.id} onClick={() => send(null, q.id)}
                            data-testid={`ai-qr-${q.id.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
                            className="rounded-full border border-saffron/40 bg-white px-3 py-1.5 text-xs font-semibold text-saffron shadow-sm transition-colors hover:bg-saffron hover:text-white">
                      {q.label}
                    </button>
                  ))}
                </div>
              )}

              <form onSubmit={(e) => { e.preventDefault(); const v = input.trim(); if (v) send(v, null); }}
                    className="flex items-center gap-2 border-t border-slate-200 bg-white p-3 sm:rounded-b-2xl">
                <input value={input} onChange={(e) => setInput(e.target.value)} data-testid="ai-chat-input"
                       placeholder="Type your message…"
                       className="w-full rounded-full border border-slate-200 bg-slate-50 px-4 py-2.5 text-sm text-navy outline-none focus:border-saffron" />
                <button type="submit" disabled={typing} data-testid="ai-send-btn" aria-label="Send"
                        className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-saffron text-white shadow-md shadow-saffron/30 transition-colors hover:bg-saffron-hover disabled:opacity-50">
                  <Send className="h-4 w-4" />
                </button>
              </form>
            </>
          )}
        </div>
      )}
    </>
  );
}
